import {
  WSButton,
  WSEmptyState,
  WSFilters,
  WSFlexBox,
  WSInfiniteScroll,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { BatchType } from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";
import { useMemo } from "react";

import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { useUrlQueryFilters } from "../../../../utils/router";
import {
  routeHelpersImportContractorsBatch,
  routeHelpersImportContractorsStart
} from "../../paths";
import { selectorImportBatchesCreatedAtFilter } from "../../selectors/selectorImportBatchesCreatedAtFilter";
import { ImportBatchesFilters } from "../../types";
import { buildImportBatchFiltersConfig } from "../importBatchFilters/config";
import {
  ImportBatchFilter,
  STATUS_FILTER_ALL
} from "../importBatchFilters/types";
import { TableContractorImportBatches } from "./TableContractorImportBatches";

export const FilterableTableContractorImportBatches: React.FC = () => {
  const { urlQuery, setUrlQuery } = useUrlQuery<ImportBatchFilter>();
  const { filters } = useUrlQueryFilters<ImportBatchesFilters>({});
  const filtersConfig = useMemo(() => buildImportBatchFiltersConfig(), []);
  const goToBatch = routeHelpersImportContractorsBatch.useGoTo();
  const goToStart = routeHelpersImportContractorsStart.useGoTo();

  const status =
    urlQuery.filter.status && urlQuery.filter.status !== STATUS_FILTER_ALL
      ? urlQuery.filter.status
      : undefined;
  const hasFilters = !!status;

  const queryBatches = queriesBatches.listBatches.usePaginatedQuery({
    filter: {
      type: { eq: BatchType.PAYEE_IMPORT },
      createdAt: selectorImportBatchesCreatedAtFilter(filters.year),
      ...(status ? { status: { anyOf: [status] } } : {})
    }
  });
  const batches = queryBatches.flatData ?? [];
  const totalCount = queryBatches.data?.pages?.[0]?.pagination?.totalSize;

  const renderTable = () => {
    if (queryBatches.isLoading) {
      return <TableContractorImportBatches batches={[]} loading />;
    }

    if (queryBatches.isError) {
      return (
        <WSEmptyState
          type="error"
          title="Could not load imports"
          description="Something went wrong loading your contractor imports."
          actions={{
            buttons: [
              {
                label: "Try again",
                kind: "Secondary",
                onClick: () => queryBatches.refetch()
              }
            ]
          }}
        />
      );
    }

    if (batches.length === 0 && hasFilters) {
      return (
        <WSEmptyState
          type="search"
          title="No imports match your filters"
          description="Try clearing the import status filter."
          actions={{
            buttons: [
              {
                label: "Clear filters",
                kind: "Secondary",
                onClick: () => setUrlQuery(prev => ({ ...prev, filter: {} }))
              }
            ]
          }}
        />
      );
    }

    return (
      <WSInfiniteScroll
        onLoad={() => queryBatches.fetchNextPage()}
        loadMore={batches.length > 0}
        endOfList={!queryBatches.hasNextPage}
        loading={!!queryBatches.isFetchingNextPage}
      >
        <TableContractorImportBatches
          batches={batches}
          bottomMessage={
            queryBatches.isFetched && batches.length === 0 ? (
              <WSFlexBox.Center direction="column">
                <WSText mt="2XL">No imports found. </WSText>
                <WSButton.Link onClick={() => goToStart()}>
                  Import contractors
                </WSButton.Link>
              </WSFlexBox.Center>
            ) : null
          }
          onRowClick={batch => goToBatch({ batchId: batch.id as string })}
        />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="2XL">
      <WSFilters<ImportBatchFilter>
        values={urlQuery.filter}
        onFilter={filter => setUrlQuery(prev => ({ ...prev, filter }))}
        primaryFilters={filtersConfig}
        info={{ count: totalCount ?? 0 }}
      />
      {renderTable()}
    </WSList>
  );
};
